import client from "@/src/apollo/client";
import GenericHead from "@/src/components/Head/GenericHead";
import LoadMoreContainer from "@/src/components/LoadMoreContainer";
import Lozenge from "@/src/components/accoutrements/lozenge";
import Slash from "@/src/components/accoutrements/slash";
import FlagContainer from "@/src/components/blocks/FlagContainer";
import { GetCategoryBySlug } from "@/src/gql/queries/category";
import { GET_CATEGORY_POSTS_PAGINATED } from "@/src/gql/queries/posts";
import { CategoryType, NavData } from "@/src/types";
import { Typography } from "@material-tailwind/react";
import { GetStaticProps } from "next";
import Link from "next/link";
import { getYoutubeVideosFromSingleChannel, YoutubeVideo } from "@/src/utils/youtube";
import { getSingleSpotifyShow } from "@/src/utils/spotify";
import FrontPageVideos from "@/src/components/FrontPage/FrontPageVideos";
import { SpotifyContainer, SpofiyShow } from "@/src/components/Podcast/Spotify";
import Container from "@/src/components/blocks/TopPosts.tsx/Container";

export interface PreviewPostProps {
  posts: any[];
  categoryName: string;
  categoryURI: string;
  extraCategories: { [name: string]: CategoryType };
  initialCursor: string;
  initialIds: string[];
  videos: YoutubeVideo[];
  spotifyShows: SpofiyShow[];
  pinnedArticles: any[];
}

const CATEGORY_TO_RICH_MEDIA = {
  'nba': {'youtube': "UULFCk2T1NMUPVK8oexgUd8tsQ", 'spotify': "5USQb597B2EDbJNInd2JSh"},
  'cricket': {'youtube': "UULFzP2XMef8PhZX0BI6_z6L4w", 'spotify': "3r9HNosFE182MGCVzoPgi3"},
  'bbl': {'youtube': "UULFzP2XMef8PhZX0BI6_z6L4w", 'spotify': "3r9HNosFE182MGCVzoPgi3"},
  'nrl': {'youtube': "UULFMReymNfmodE1OEc9fQvwQQ", 'spotify': "7LT4jzjyZQGui4P8xduwNU"},
  'epl': {'youtube': "UULFcaTPguGCmg8awoRPTRO1Mg", 'spotify': "5ZBczHmD9sReJ0Hr435PWB"},
  'nfl': {'youtube': "UULFmMgv95hbFGQujDy8nFBYJQ", 'spotify': "6l66uODx8RZXfyctKoZtw9"},
}

const CategoryHome = ({
  posts,
  categoryName,
  categoryURI,
  extraCategories = {},
  initialCursor,
  initialIds,
  videos = [],
  spotifyShows = [],
  pinnedArticles = [],
}: PreviewPostProps) => {
  const flagArticles = posts?.slice(0, 3);
  const flagArticleContainer = flagArticles?.map(function (article, index) {
    return {
      position: index === 0 ? 0 : index === 1 ? 1 : 2,
      article: { heroArticlePostItem: { nodes: [article] } },
    };
  });
  
  return (
    <>
      <GenericHead
        title={`SCPB ${categoryName} Supercoach News`}
        description={`SCPB ${categoryName} SuperCoach news`}
      />
      <div>
        <div className="mx-auto flex flex-col gap-4">
          {
            // @ts-ignore
            <FlagContainer flags={flagArticleContainer} />
          }
        </div>
        {videos?.length > 0 && (
          <FrontPageVideos header={`${categoryName} Videos`} videos={videos} />
        )}
        {pinnedArticles?.length > 0 && (
          <div className="bg-white front-page-container pt-4">
            <div className="container mx-auto">
              <Container
                posts={pinnedArticles}
              />
            </div>
          </div>
        )}
        {spotifyShows?.length > 0 && (
          <div className="w-full bg-white">
            <div className="container mx-auto py-4">
              <SpotifyContainer shows={spotifyShows} />
            </div>
          </div>
        )}
        <div className="w-[100%] bg-white">
          <div className="container mx-auto flex flex-col gap-4 py-4">
            <div className="flex items-center text-2xl gap-2 px-2 md:px-0">
              <Typography variant="h2" className="whitespace-nowrap	">
                {categoryName}
              </Typography>
              <Slash />
              <div
                className={"flex items-center w-full overflow-scroll"}
                style={{ columnGap: "0.5rem" }}
              >
                {Object.values(extraCategories)
                  .slice(0, 5)
                  .map(function (category) {
                    return (
                      <Link
                        href={`${category.uri}`}
                        key={category.uri}
                        className="flex"
                      >
                        <Lozenge color="light" text={category.name} />
                      </Link>
                    );
                  })}
              </div>
            </div>
            <LoadMoreContainer
              initialArticles={posts?.slice(3)}
              ignoreIds={initialIds}
              loadMoreQuery={GET_CATEGORY_POSTS_PAGINATED}
              loadMoreVariables={{ id: categoryURI }}
              convertPayload={(object: any) => {
                const thelastCursor = object?.category?.posts?.edges
                  ? object?.category?.posts?.edges[
                      object?.category?.posts?.edges.length - 1
                    ]?.cursor
                  : "";
                return {
                  posts: object?.category?.posts?.edges.map(function (
                    article: any
                  ) {
                    return article.node;
                  }),
                  thelastCursor: object?.category?.posts?.edges
                    ? object?.category?.posts?.edges[
                        object?.category?.posts?.edges.length - 1
                      ]?.cursor
                    : "",
                  done: false,
                };
              }}
              initialCursor={initialCursor}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const theSlug = context.params?.slug?.length
    ? context.params?.slug[context.params?.slug?.length - 1]
    : "";
  const category = await client.query({
    query: GetCategoryBySlug,
    variables: { id: `/category/${theSlug}` },
  });

  if (!category) {
    return {
      props: { category },
      notFound: true,
    };
  }

  // Extract posts from first page
  let posts =
    category.data?.category?.posts?.edges.map(function (edge: object) {
      //@ts-ignore
      return edge.node;
    }) || [];

  // Get the cursor from the last post of the first page
  let initialCursor =
    category?.data?.category?.posts?.edges?.[
      category.data.category.posts.edges.length - 1
    ]?.cursor || "";

  // Fetch second page if we have posts and a valid cursor
  if (posts.length > 0 && initialCursor) {
    try {
      const secondPage = await client.query({
        query: GET_CATEGORY_POSTS_PAGINATED,
        variables: { 
          id: `/category/${theSlug}`,
          after: initialCursor 
        },
      });

      const secondPagePosts =
        secondPage.data?.category?.posts?.edges.map(function (edge: object) {
          //@ts-ignore
          return edge.node;
        }) || [];

      if (secondPagePosts.length > 0) {
        posts = [...posts, ...secondPagePosts];
        
        // Update cursor to the last post of the second page
        initialCursor =
          secondPage?.data?.category?.posts?.edges?.[
            secondPage.data.category.posts.edges.length - 1
          ]?.cursor || initialCursor;
        
        console.log('posts page 2:', secondPagePosts.length);
      }
    } catch (error) {
      console.error('Error fetching second page:', error);
    }
  }


  
  // Separate pinned articles from regular posts
  const pinnedArticles = posts.filter((post: any) => 
    post.articleOptions?.categoryPinnedRank != null && 
    typeof post.articleOptions.categoryPinnedRank === 'number' &&
    post.articleOptions.categoryPinnedRank > 0
  ).sort((a: any, b: any) => 
    a.articleOptions.categoryPinnedRank - b.articleOptions.categoryPinnedRank
  );

  // Remove pinned articles from regular posts
  const pinnedIds = new Set(pinnedArticles.map((post: any) => post.id));
  posts = posts.filter((post: any) => !pinnedIds.has(post.id));
  
  const extraCategories: { [name: string]: CategoryType } = {};
  const categoryName = category?.data?.category?.name || "";
  const ids: string[] = [];
  
  // Collect IDs and categories from both pinned and regular posts
  [...pinnedArticles, ...posts].forEach(function (post: any) {
    if (post.categories?.edges) {
      post.categories?.edges.forEach(function (category: any) {
        if (category.node.name !== categoryName) {
          extraCategories[category.node.name] = {
            name: category.node.name,
            uri: category.node.uri,
          };
        }
      });
    }
    ids.push(post.id);
  });

  const categoryURI = `/category/${theSlug}`;
  
  // Fetch rich media if category is in CATEGORY_TO_RICH_MEDIA
  let videos: YoutubeVideo[] = [];
  let spotifyShows: SpofiyShow[] = [];
  
  if (theSlug && CATEGORY_TO_RICH_MEDIA[theSlug as keyof typeof CATEGORY_TO_RICH_MEDIA]) {
    const richMedia = CATEGORY_TO_RICH_MEDIA[theSlug as keyof typeof CATEGORY_TO_RICH_MEDIA];
    
    // Fetch YouTube videos
    if (richMedia.youtube) {
      try {
        videos = await getYoutubeVideosFromSingleChannel(richMedia.youtube);
        // Limit to 5 videos
        videos = videos.slice(0, 7);
      } catch (error) {
        console.error('Error fetching YouTube videos:', error);
        videos = [];
      }
    }
    // Fetch Spotify episodes
    if (richMedia.spotify) {
      try {
        spotifyShows = await getSingleSpotifyShow(richMedia.spotify, 5);
      } catch (error) {
        console.error('Error fetching Spotify episodes:', error);
        spotifyShows = [];
      }
    }
  }
  
  return {
    props: {
      posts,
      categoryName,
      categoryURI,
      extraCategories,
      initialCursor,
      initialIds: ids,
      videos,
      spotifyShows,
      pinnedArticles,
    },
    revalidate: 60 * 30,
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export default CategoryHome;
